/* eslint-disable max-lines -- not split yet: HH:MM time helpers, widen selection state, tier and error mapping */
import { TZDate } from '@date-fns/tz';
import { t } from '@lingui/core/macro';
import {
  BookingRecordDetailsCustomer,
  BookingUpdateOfferPublicRecord,
  BookingUpdateRequestDiscountTierType,
  DeliveryWindowDateRecord
} from '@navio/nsdk';
import { NoddiAsyncError } from 'noddi-types';
import {
  BOOKING_UPDATE_OFFER_WIDEN_LOCAL_BAND_END,
  BOOKING_UPDATE_OFFER_WIDEN_LOCAL_BAND_START,
  compareBookingUpdateDiscountTierThresholds,
  formatClockTime,
  formatCurrencyAmount,
  formatWholeMinutesAsTimeString,
  getFirstErrorCode,
  NoddiDateFormats,
  noddiFormatDate,
  zonedWallClockIso
} from 'noddi-util';

export const getSalesItemIds = (booking: BookingRecordDetailsCustomer): number[] =>
  booking.bookingItemsCar.flatMap((car) =>
    (car.orderLines ?? []).flatMap((line) => (line.salesItemId != null ? [line.salesItemId] : []))
  );

export const getWidenDateForApi = ({
  offer,
  booking
}: {
  offer: BookingUpdateOfferPublicRecord | null | undefined;
  booking: BookingRecordDetailsCustomer;
}) => {
  const serviceDate = offer?.serviceDate ?? booking.date;
  return typeof serviceDate === 'string' && serviceDate.length >= 10 ? serviceDate.slice(0, 10) : '';
};

const toBookingLocalTimeInputValue = (iso: string, timeZoneValue: string) => {
  return noddiFormatDate(new TZDate(iso, timeZoneValue), NoddiDateFormats.TIME);
};

const formatNaiveTimeLabel = (value: string | null | undefined) => {
  if (value == null || value === '') {
    return null;
  }
  return formatClockTime(value);
};

const timeHmToMinutes = (hm: string) => {
  const parts = hm.split(':');
  const h = Number.parseInt(parts[0] ?? '', 10);
  const m = Number.parseInt(parts[1] ?? '', 10);
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return 0;
  }
  return h * 60 + m;
};

const minutesFromMidnightToHm = (minutesFromMidnight: number): string => {
  const wrapped = ((minutesFromMidnight % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const addMinutesToHm = (hm: string, deltaMinutes: number): string =>
  minutesFromMidnightToHm(timeHmToMinutes(hm) + deltaMinutes);

const clampHmMin = (hm: string, minHm: string): string => (timeHmToMinutes(hm) < timeHmToMinutes(minHm) ? minHm : hm);

const clampHmMax = (hm: string, maxHm: string): string => (timeHmToMinutes(hm) > timeHmToMinutes(maxHm) ? maxHm : hm);

/** Native time inputs often ignore min/max; keep state aligned with widen bounds. */
export const clampHmToRange = (hm: string, minHm: string, maxHm: string): string =>
  clampHmMin(clampHmMax(hm, maxHm), minHm);

export const WIDEN_TIME_SELECT_STEP_MINUTES = 15;

export type WidenTimeSelectOption = {
  value: string;
  label: string;
};

/** Discrete HH:MM options between min and max (inclusive); avoids native time input hour/minute bugs. */
export const buildWidenTimeSelectOptions = (
  minHm: string,
  maxHm: string,
  stepMinutes: number
): WidenTimeSelectOption[] => {
  const minM = timeHmToMinutes(minHm);
  const maxM = timeHmToMinutes(maxHm);
  if (minM > maxM) {
    return [];
  }
  const minutesSet = new Set<number>();
  // eslint-disable-next-line no-restricted-syntax -- stepping loop counter
  for (let m = minM; m <= maxM; m += stepMinutes) {
    minutesSet.add(m);
  }
  minutesSet.add(minM);
  minutesSet.add(maxM);
  return [...minutesSet]
    .filter((m) => m >= minM && m <= maxM)
    .sort((a, b) => a - b)
    .map((m) => {
      const hm = minutesFromMidnightToHm(m);
      return { value: hm, label: hm };
    });
};

type WidenUiBounds = {
  currentStart: string;
  currentEnd: string;
  staffLimitsFeasibleWithBooking: boolean;
  showStartEditor: boolean;
  showEndEditor: boolean;
  startInputMin: string;
  startInputMax: string;
  endInputMin: string;
  endInputMax: string;
};

export type OfferDiscountTier = BookingUpdateOfferPublicRecord['discountTiers'][number];

type WidenWindowMatch = {
  deliveryWindowId: number;
  startsAt: string;
  endsAt: string;
};

type WidenSelectionState = {
  resolvedWidenStartTime: string;
  resolvedWidenEndTime: string;
  flatWidenWindows: WidenWindowMatch[];
  widenWindowMatch: WidenWindowMatch | undefined;
  matchedDeliveryWindowId: number | undefined;
  useCustomWidenTimes: boolean;
  customWidenTimesValid: boolean;
  widenExpansionMinutes: number;
  widenEarnedDiscountLabel: string | null;
  widenNextTierNudgeDescription: string | null;
};

/**
 * Anchored to the address's zone, not the browser's: a customer whose device is
 * on another timezone would otherwise widen their window to a time hours from
 * the one the picker showed them.
 */
export const localWallTimeOnServiceDateToUtcIso = (
  serviceDateYmd: string,
  hm: string,
  timeZoneValue: string
): string => {
  const parts = hm.split(':');
  const hours = Number.parseInt(parts[0] ?? '', 10);
  const minutes = Number.parseInt(parts[1] ?? '', 10);
  return zonedWallClockIso(
    serviceDateYmd,
    { hours: Number.isNaN(hours) ? 0 : hours, minutes: Number.isNaN(minutes) ? 0 : minutes },
    timeZoneValue
  );
};

export const getWidenUiBounds = ({
  offer,
  booking
}: {
  offer: BookingUpdateOfferPublicRecord;
  booking: BookingRecordDetailsCustomer;
}): WidenUiBounds => {
  const tz = booking.address.timezone.value;
  const currentStart = toBookingLocalTimeInputValue(offer.deliveryWindowStartsAt, tz);
  const currentEnd = toBookingLocalTimeInputValue(offer.deliveryWindowEndsAt, tz);
  const bandStart = formatNaiveTimeLabel(BOOKING_UPDATE_OFFER_WIDEN_LOCAL_BAND_START);
  const bandEnd = formatNaiveTimeLabel(BOOKING_UPDATE_OFFER_WIDEN_LOCAL_BAND_END);

  const showStartEditor =
    bandStart == null
      ? timeHmToMinutes(currentStart) > timeHmToMinutes('06:00')
      : timeHmToMinutes(currentStart) > timeHmToMinutes(bandStart);

  const showEndEditor =
    bandEnd == null
      ? timeHmToMinutes(currentEnd) < timeHmToMinutes('23:00')
      : timeHmToMinutes(currentEnd) < timeHmToMinutes(bandEnd);

  const staffLimitsFeasibleWithBooking = showStartEditor || showEndEditor;

  return {
    currentStart,
    currentEnd,
    staffLimitsFeasibleWithBooking,
    showStartEditor,
    showEndEditor,
    startInputMin: bandStart ?? '06:00',
    startInputMax: currentStart,
    endInputMin: currentEnd,
    endInputMax: bandEnd ?? '23:00'
  };
};

export const getWidenFeasibleDiscountTiers = ({
  offer,
  widenUiBounds
}: {
  offer: BookingUpdateOfferPublicRecord;
  widenUiBounds: WidenUiBounds | null;
}) =>
  !offer.widenOffered || widenUiBounds == null
    ? offer.discountTiers
    : offer.discountTiers.filter((tier) => tierIsAchievableWithinWidenBand(tier, widenUiBounds));

const resolveWidenTimeValue = ({
  selectedTime,
  currentTime,
  selectOptions,
  isEditorVisible,
  fallbackWhenInvalid
}: {
  selectedTime: string;
  currentTime: string;
  selectOptions: WidenTimeSelectOption[];
  isEditorVisible: boolean;
  fallbackWhenInvalid: string;
}) => {
  if (!isEditorVisible) {
    return currentTime;
  }

  if (selectOptions.length === 0) {
    return selectedTime;
  }

  const allowedValues = new Set(selectOptions.map(({ value }) => value));
  const preferredTime = selectedTime === '' ? currentTime : selectedTime;

  return allowedValues.has(preferredTime) ? preferredTime : fallbackWhenInvalid;
};

const getResolvedWidenTimes = ({
  widenUiBounds,
  windowsQueryEnabled,
  widenStartTime,
  widenEndTime,
  widenStartTimeSelectOptions,
  widenEndTimeSelectOptions
}: {
  widenUiBounds: WidenUiBounds | null;
  windowsQueryEnabled: boolean;
  widenStartTime: string;
  widenEndTime: string;
  widenStartTimeSelectOptions: WidenTimeSelectOption[];
  widenEndTimeSelectOptions: WidenTimeSelectOption[];
}) => {
  if (widenUiBounds == null || !windowsQueryEnabled) {
    return {
      resolvedWidenStartTime: widenStartTime,
      resolvedWidenEndTime: widenEndTime
    };
  }

  return {
    resolvedWidenStartTime: resolveWidenTimeValue({
      selectedTime: widenStartTime,
      currentTime: widenUiBounds.currentStart,
      selectOptions: widenStartTimeSelectOptions,
      isEditorVisible: widenUiBounds.showStartEditor,
      fallbackWhenInvalid: widenUiBounds.startInputMax
    }),
    resolvedWidenEndTime: resolveWidenTimeValue({
      selectedTime: widenEndTime,
      currentTime: widenUiBounds.currentEnd,
      selectOptions: widenEndTimeSelectOptions,
      isEditorVisible: widenUiBounds.showEndEditor,
      fallbackWhenInvalid: widenUiBounds.endInputMin
    })
  };
};

const flattenDeliveryWindows = ({
  deliveryWindowDates
}: {
  deliveryWindowDates: DeliveryWindowDateRecord[] | undefined;
}): WidenWindowMatch[] =>
  (deliveryWindowDates ?? []).flatMap((deliveryWindowDate) =>
    deliveryWindowDate.timeSlots.flatMap((timeSlot) => {
      const deliveryWindow = timeSlot.deliveryWindow;

      return deliveryWindow?.deliveryWindowId != null &&
        deliveryWindow.startsAt != null &&
        deliveryWindow.endsAt != null
        ? [
            {
              deliveryWindowId: deliveryWindow.deliveryWindowId,
              startsAt: deliveryWindow.startsAt,
              endsAt: deliveryWindow.endsAt
            }
          ]
        : [];
    })
  );

const getWidenWindowMatch = ({
  widenUiBounds,
  resolvedWidenStartTime,
  resolvedWidenEndTime,
  flatWidenWindows,
  bookingTimeZone
}: {
  widenUiBounds: WidenUiBounds | null;
  resolvedWidenStartTime: string;
  resolvedWidenEndTime: string;
  flatWidenWindows: WidenWindowMatch[];
  bookingTimeZone: string;
}): WidenWindowMatch | undefined => {
  if (
    widenUiBounds == null ||
    resolvedWidenStartTime === '' ||
    resolvedWidenEndTime === '' ||
    flatWidenWindows.length === 0
  ) {
    return undefined;
  }

  const coveringWindows = flatWidenWindows.filter((deliveryWindow) => {
    const localStartTime = toBookingLocalTimeInputValue(deliveryWindow.startsAt, bookingTimeZone);
    const localEndTime = toBookingLocalTimeInputValue(deliveryWindow.endsAt, bookingTimeZone);

    return localStartTime <= widenUiBounds.currentStart && localEndTime >= widenUiBounds.currentEnd;
  });

  if (coveringWindows.length === 0) {
    return undefined;
  }

  const selectedStartMinutes = timeHmToMinutes(resolvedWidenStartTime);
  const selectedEndMinutes = timeHmToMinutes(resolvedWidenEndTime);

  const closestWindow = coveringWindows.reduce<
    | (WidenWindowMatch & {
        distance: number;
      })
    | undefined
  >((bestMatch, deliveryWindow) => {
    const localStartTime = toBookingLocalTimeInputValue(deliveryWindow.startsAt, bookingTimeZone);
    const localEndTime = toBookingLocalTimeInputValue(deliveryWindow.endsAt, bookingTimeZone);
    const distance =
      Math.abs(timeHmToMinutes(localStartTime) - selectedStartMinutes) +
      Math.abs(timeHmToMinutes(localEndTime) - selectedEndMinutes);

    if (bestMatch == null || distance < bestMatch.distance) {
      return {
        ...deliveryWindow,
        distance
      };
    }

    return bestMatch;
  }, undefined);

  if (closestWindow == null) {
    return undefined;
  }

  return {
    deliveryWindowId: closestWindow.deliveryWindowId,
    startsAt: closestWindow.startsAt,
    endsAt: closestWindow.endsAt
  };
};

const getWidenExpansion = ({
  widenUiBounds,
  resolvedWidenStartTime,
  resolvedWidenEndTime
}: {
  widenUiBounds: WidenUiBounds | null;
  resolvedWidenStartTime: string;
  resolvedWidenEndTime: string;
}) => {
  const earlierMinutes =
    widenUiBounds == null || resolvedWidenStartTime === ''
      ? 0
      : Math.max(0, timeHmToMinutes(widenUiBounds.currentStart) - timeHmToMinutes(resolvedWidenStartTime));

  const laterMinutes =
    widenUiBounds == null || resolvedWidenEndTime === ''
      ? 0
      : Math.max(0, timeHmToMinutes(resolvedWidenEndTime) - timeHmToMinutes(widenUiBounds.currentEnd));

  return {
    earlierMinutes,
    laterMinutes,
    expansionMinutes: earlierMinutes + laterMinutes
  };
};

const getWidenEarnedDiscountLabel = ({
  tiers,
  earlierMinutes,
  laterMinutes
}: {
  tiers: OfferDiscountTier[];
  earlierMinutes: number;
  laterMinutes: number;
}) => {
  if (tiers.length === 0) {
    return null;
  }

  const bestQualifiedTier = [...tiers]
    .sort(compareBookingUpdateDiscountTierThresholds)
    .filter((tier) => tierQualifiesForWiden(tier, earlierMinutes, laterMinutes))
    .at(-1);

  return bestQualifiedTier == null ? null : getTierDiscountLabel(bestQualifiedTier);
};

const getWidenNextTierNudgeDescription = ({
  tiers,
  widenUiBounds,
  earlierMinutes,
  laterMinutes
}: {
  tiers: OfferDiscountTier[];
  widenUiBounds: WidenUiBounds | null;
  earlierMinutes: number;
  laterMinutes: number;
}) => {
  if (tiers.length === 0 || widenUiBounds == null) {
    return null;
  }

  const nextTier = [...tiers]
    .sort(compareBookingUpdateDiscountTierThresholds)
    .find((tier) => !tierQualifiesForWiden(tier, earlierMinutes, laterMinutes));

  if (nextTier == null) {
    return null;
  }

  const discountLabel = getTierDiscountLabel(nextTier);

  if (discountLabel == null) {
    return null;
  }

  const totalShortfallMinutes = Math.max(
    0,
    Math.ceil(nextTier.minTotalExpansionSeconds / 60) - earlierMinutes - laterMinutes
  );
  const earlierShortfallMinutes =
    nextTier.minExpansionEarlierSeconds == null
      ? 0
      : Math.max(0, Math.ceil(nextTier.minExpansionEarlierSeconds / 60) - earlierMinutes);
  const laterShortfallMinutes =
    nextTier.minExpansionLaterSeconds == null
      ? 0
      : Math.max(0, Math.ceil(nextTier.minExpansionLaterSeconds / 60) - laterMinutes);
  const gapMinutes = Math.max(totalShortfallMinutes, earlierShortfallMinutes, laterShortfallMinutes);

  if (
    earlierShortfallMinutes === gapMinutes &&
    earlierShortfallMinutes > 0 &&
    nextTier.minExpansionEarlierSeconds != null
  ) {
    const requiredEarlierMinutes = Math.ceil(nextTier.minExpansionEarlierSeconds / 60);
    const suggestedTime = clampHmMin(
      addMinutesToHm(widenUiBounds.currentStart, -requiredEarlierMinutes),
      widenUiBounds.startInputMin
    );

    return t`Start from ${suggestedTime} to get ${discountLabel} off`;
  }

  if (laterShortfallMinutes === gapMinutes && laterShortfallMinutes > 0 && nextTier.minExpansionLaterSeconds != null) {
    const requiredLaterMinutes = Math.ceil(nextTier.minExpansionLaterSeconds / 60);
    const suggestedTime = clampHmMax(
      addMinutesToHm(widenUiBounds.currentEnd, requiredLaterMinutes),
      widenUiBounds.endInputMax
    );

    return t`Extend to ${suggestedTime} to get ${discountLabel} off`;
  }

  const extraWideningLabel = formatWholeMinutesAsTimeString(gapMinutes);

  return t`Add ${extraWideningLabel} more to get ${discountLabel} off`;
};

const isCustomWidenTimeSelectionValid = ({
  widenDateForApi,
  resolvedWidenStartTime,
  resolvedWidenEndTime,
  bookingTimeZone
}: {
  widenDateForApi: string;
  resolvedWidenStartTime: string;
  resolvedWidenEndTime: string;
  bookingTimeZone: string;
}) => {
  if (widenDateForApi === '' || resolvedWidenStartTime === '' || resolvedWidenEndTime === '') {
    return false;
  }

  const startTime = Date.parse(
    localWallTimeOnServiceDateToUtcIso(widenDateForApi, resolvedWidenStartTime, bookingTimeZone)
  );
  const endTime = Date.parse(
    localWallTimeOnServiceDateToUtcIso(widenDateForApi, resolvedWidenEndTime, bookingTimeZone)
  );

  return Number.isFinite(startTime) && Number.isFinite(endTime) && startTime < endTime;
};

export const getWidenSelectionState = ({
  widenUiBounds,
  windowsQueryEnabled,
  widenStartTime,
  widenEndTime,
  widenStartTimeSelectOptions,
  widenEndTimeSelectOptions,
  deliveryWindowDates,
  bookingTimeZone,
  widenDateForApi,
  widenFeasibleDiscountTiers
}: {
  widenUiBounds: WidenUiBounds | null;
  windowsQueryEnabled: boolean;
  widenStartTime: string;
  widenEndTime: string;
  widenStartTimeSelectOptions: WidenTimeSelectOption[];
  widenEndTimeSelectOptions: WidenTimeSelectOption[];
  deliveryWindowDates: DeliveryWindowDateRecord[] | undefined;
  bookingTimeZone: string;
  widenDateForApi: string;
  widenFeasibleDiscountTiers: OfferDiscountTier[];
}): WidenSelectionState => {
  const { resolvedWidenStartTime, resolvedWidenEndTime } = getResolvedWidenTimes({
    widenUiBounds,
    windowsQueryEnabled,
    widenStartTime,
    widenEndTime,
    widenStartTimeSelectOptions,
    widenEndTimeSelectOptions
  });
  const flatWidenWindows = flattenDeliveryWindows({ deliveryWindowDates });
  const widenWindowMatch = getWidenWindowMatch({
    widenUiBounds,
    resolvedWidenStartTime,
    resolvedWidenEndTime,
    flatWidenWindows,
    bookingTimeZone
  });
  const useCustomWidenTimes =
    widenWindowMatch != null &&
    resolvedWidenStartTime !== '' &&
    resolvedWidenEndTime !== '' &&
    (toBookingLocalTimeInputValue(widenWindowMatch.startsAt, bookingTimeZone) !== resolvedWidenStartTime ||
      toBookingLocalTimeInputValue(widenWindowMatch.endsAt, bookingTimeZone) !== resolvedWidenEndTime);
  const customWidenTimesValid = useCustomWidenTimes
    ? isCustomWidenTimeSelectionValid({
        widenDateForApi,
        resolvedWidenStartTime,
        resolvedWidenEndTime,
        bookingTimeZone
      })
    : false;
  const { earlierMinutes, laterMinutes, expansionMinutes } = getWidenExpansion({
    widenUiBounds,
    resolvedWidenStartTime,
    resolvedWidenEndTime
  });

  return {
    resolvedWidenStartTime,
    resolvedWidenEndTime,
    flatWidenWindows,
    widenWindowMatch,
    matchedDeliveryWindowId: widenWindowMatch?.deliveryWindowId,
    useCustomWidenTimes,
    customWidenTimesValid,
    widenExpansionMinutes: expansionMinutes,
    widenEarnedDiscountLabel: getWidenEarnedDiscountLabel({
      tiers: widenFeasibleDiscountTiers,
      earlierMinutes,
      laterMinutes
    }),
    widenNextTierNudgeDescription: getWidenNextTierNudgeDescription({
      tiers: widenFeasibleDiscountTiers,
      widenUiBounds,
      earlierMinutes,
      laterMinutes
    })
  };
};

const getResponseStatusFromError = (e: unknown): number | undefined => (e as NoddiAsyncError)?.response?.status;

export const getAcceptWidenUserFacingError = (e: unknown): string | null => {
  if (getResponseStatusFromError(e) !== 400) {
    return null;
  }
  const code = getFirstErrorCode(e as NoddiAsyncError);
  if (code === 'booking_update_offer_window_not_expanded') {
    return t`The new window can't be smaller than your current one.`;
  }
  if (code === 'booking_update_offer_bounds_invalid') {
    return t`That time doesn't fit the available range. Pick a window within the times shown above.`;
  }
  return t`This request is no longer available.`;
};

const tierQualifiesForWiden = (tier: OfferDiscountTier, earlierMinutes: number, laterMinutes: number): boolean => {
  const totalSec = (earlierMinutes + laterMinutes) * 60;
  if (totalSec < tier.minTotalExpansionSeconds) {
    return false;
  }
  if (tier.minExpansionEarlierSeconds != null && earlierMinutes * 60 < tier.minExpansionEarlierSeconds) {
    return false;
  }
  if (tier.minExpansionLaterSeconds != null && laterMinutes * 60 < tier.minExpansionLaterSeconds) {
    return false;
  }
  return true;
};

const getWidenBandMaxExpansionMinutes = (bounds: WidenUiBounds): { maxEarlier: number; maxLater: number } => {
  const maxEarlier = bounds.showStartEditor
    ? Math.max(0, timeHmToMinutes(bounds.currentStart) - timeHmToMinutes(bounds.startInputMin))
    : 0;
  const maxLater = bounds.showEndEditor
    ? Math.max(0, timeHmToMinutes(bounds.endInputMax) - timeHmToMinutes(bounds.currentEnd))
    : 0;
  return { maxEarlier, maxLater };
};

/** True if some (earlier, later) choice within the widen UI can ever satisfy this tier. */
const tierIsAchievableWithinWidenBand = (tier: OfferDiscountTier, bounds: WidenUiBounds): boolean => {
  const { maxEarlier, maxLater } = getWidenBandMaxExpansionMinutes(bounds);
  const eReq = tier.minExpansionEarlierSeconds != null ? Math.ceil(tier.minExpansionEarlierSeconds / 60) : 0;
  const lReq = tier.minExpansionLaterSeconds != null ? Math.ceil(tier.minExpansionLaterSeconds / 60) : 0;
  const tReq = Math.ceil(tier.minTotalExpansionSeconds / 60);
  const sumFloor = Math.max(tReq, eReq + lReq);
  return eReq <= maxEarlier && lReq <= maxLater && sumFloor <= maxEarlier + maxLater;
};

export const getTierDiscountLabel = (tier: OfferDiscountTier): string | null => {
  if (tier.discountType === BookingUpdateRequestDiscountTierType.FIXED && tier.discount != null) {
    return formatCurrencyAmount({ money: tier.discount, decimals: 0 });
  }
  if (tier.discountPercentage != null) {
    return `${tier.discountPercentage}%`;
  }
  return null;
};

export const getDeclineOfferUserFacingError = (e: unknown): string | null =>
  getResponseStatusFromError(e) === 400 ? t`This can no longer be updated.` : null;
